import { Component, type ReactNode } from "react";
import { reportError } from "../lib/errorReporting";
import { Notice } from "./ui";

/** A lazy part of the app (a tool, its windows) that couldn't be loaded: after a deploy, the old code's files are gone. */
const isChunkError = (err: unknown) => err instanceof Error && /dynamically imported module|Importing a module script failed|Loading chunk/i.test(err.message);

/** When it last reloaded for a missing part: at most once a minute, so a part that's really gone shows, not a reload loop. */
const RELOADED_AT = "workbench:reloaded-for-chunk";

function reloadOnce() {
    try {
        if (Date.now() - Number(sessionStorage.getItem(RELOADED_AT) ?? 0) < 60_000) return;
        sessionStorage.setItem(RELOADED_AT, String(Date.now()));
    } catch {
        // Storage blocked: no reload by itself (Reload is there).
        return;
    }
    location.reload();
}

/**
 * Something below it crashed: that part says so (with Reload), not the whole app going blank. A part that couldn't be loaded
 * (the app updated since this page opened) reloads by itself, once.
 */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
    override state: { error: Error | null } = { error: null };

    static getDerivedStateFromError(error: Error) {
        return { error };
    }

    override componentDidCatch(error: Error) {
        reportError("react", error);
        if (isChunkError(error)) reloadOnce();
    }

    override render() {
        if (!this.state.error) return this.props.children;
        const updated = isChunkError(this.state.error);
        return (
            <div className="p-4">
                <Notice
                    tone="error"
                    title="Something went wrong."
                    actions={
                        <button type="button" className="btn-secondary" onClick={() => location.reload()}>
                            Reload
                        </button>
                    }
                >
                    {updated ? "Outback Workbench has been updated since this page opened: reload to carry on." : "This part couldn't be shown. Reload to try again."}
                </Notice>
            </div>
        );
    }
}
